import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { template } from 'lodash';
import jsCookie from 'js-cookie';
import PropTypes from 'prop-types';
import { withTheme } from '@glu/theming';
import { withFormProvider } from '@glu/validation-react';

import { sendOTPCode } from 'api/user/service';
import { logout } from 'logic/store/authentication/actions';
import { clearGrids } from 'logic/store/grids/actions';
import useLocale from 'components/useLocale';
import { WORKSPACES, HEADERS, challengeTypes } from 'constants.js';
import ModalNotification from 'components/modalNotification/ModalNotification';
import getMaskedValue from 'utils/challengeManagerMask';

import OtpForm from './OTP';
import RadiusForm from './RADIUS';
import VipForm from './VIP';
import useStyles from './ChallengeManager.styles';

const enterKey = 'Enter';
const { EMAIL, OTP, RADIUS, SMS, VIP_ACTIVATION, VIP_CHALLENGE, VIP_UNAVAILABLE, VOICE } =
  challengeTypes;

export const ChallengeManagerComponent = ({
  challengeErrorMessage,
  clearGrids,
  data,
  data: { challengeType, phoneNumber, email, voice = {}, defaultContactType } = {},
  deviceChallenge,
  logout,
  onCancel,
  onHide,
  onSubmit,
  open,
  rememberMe,
  rememberMeFlag,
  validate,
  variant
}) => {
  const locale = useLocale();
  const history = useHistory();
  const classes = useStyles();
  const [primaryMethod, setPrimaryMethod] = useState(''); // Set primary method(Email/Phone) masked value
  const [primaryMethodType, setPrimaryMethodType] = useState(''); // Set primary Method Type
  const [primaryMethodOriginal, setPrimaryMethodOriginal] = useState(''); // Set the original primary
  const [showAltMethod, setShowAltMethod] = useState(false);
  const [otpCode, setOtpCode] = useState(''); // store input value added by user
  const [vipTokens, setVipTokens] = useState({});
  const [radiusToken, setRadiusToken] = useState('');
  const [altMethodMessage, setAltMethodMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Setup an intercept for axios response for re-enabling continue button
  // TODO: ignore refresh and getSessionTimeRemaining
  useEffect(() => {
    const cmRequestInterceptor = axios.interceptors.request.use(
      (request) => ({
        ...request,
        headers: {
          ...request.headers,
          [HEADERS.X_CM_PASSCODE]: otpCode || vipTokens.token1 || radiusToken,
          [HEADERS.X_NEXTCM_PASSCODE]: vipTokens.token2
        }
      }),
      // return request;
      (error) => Promise.reject(error)
    );
    const cmResponseInterceptor = axios.interceptors.response.use(
      (response) => {
        // Ignore polling requests that are not associated to whatever API call that was challenged
        // (Basically don't re-enable the continue button from these requests resolving)
        if (!response?.data?.success) {
          setIsSubmitting(false);
        }
        return response;
      },
      (error) => {
        setIsSubmitting(false);
        return Promise.reject(error);
      }
    );
    return () => {
      // Once CM un-mounts then eject interceptors
      axios.interceptors.request.eject(cmRequestInterceptor);
      axios.interceptors.response.eject(cmResponseInterceptor);
    };
    // Update the interceptors when these values change
  }, [vipTokens.token1, vipTokens.token2, otpCode, radiusToken]);

  useEffect(() => {
    // This is to set an error message when user gets locked out and navigate to login page, need to carry the message
    if (data[0]) {
      history.push({
        pathname: WORKSPACES.DASHBOARD,
        state: {
          message: data[0].userMessage
        }
      });
      clearGrids();
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onChange = (event, gluValue) => {
    // On change of input
    const { value = gluValue } = event.target || event;
    if (challengeType === OTP) setOtpCode(value);
    if ([VIP_CHALLENGE, VIP_ACTIVATION, VIP_UNAVAILABLE].includes(challengeType)) {
      setVipTokens((state) => ({ ...state, [event]: value }));
    }
    if (challengeType.includes(RADIUS)) {
      setRadiusToken(value);
    }
  };

  const sendCodeToAltMethod = (defaultMethod, altMethodType) => {
    let altMethodVal = '';
    if (!defaultMethod) {
      if (altMethodType === SMS) altMethodVal = phoneNumber;
      if (altMethodType === EMAIL) altMethodVal = email;
      if (altMethodType === VOICE) altMethodVal = voice.unformattedvalue;
    }

    // Call send OTP code API to get code in alt method, first remove the X_CM_PASSCODE Token, very important
    jsCookie.remove(HEADERS.X_CM_PASSCODE);
    sendOTPCode({
      contactType: defaultMethod ? primaryMethodType : altMethodType,
      value: defaultMethod ? primaryMethodOriginal : altMethodVal
    }).subscribe();
    setAltMethodMessage(
      template(locale['challengeManager.passcodeOtherWay'])({
        maskedAltMethodVal: getMaskedValue({ challengeType: altMethodType, data }),
        altMethodType
      })
    );
  };

  const resendCode = () => {
    jsCookie.remove(HEADERS.X_CM_PASSCODE); // Call send OTP code API to get code again in primary method, first remove the X_CM_PASSCODE Token, very important
    sendCodeToAltMethod(true);
    setAltMethodMessage(null);
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    // If form is already submitting, then prevent additional calls
    if (!isSubmitting) {
      setIsSubmitting(true);
      onSubmit(e);
    }
  };

  const onChecked = (checked) => {
    rememberMe(checked);
  };

  const onKeyDown = (event) => {
    // If challenge manager is submitting don't allow multiple enter key hits to duplicate
    if (event.key === enterKey) {
      onSubmitForm(event);
    }
  };

  // On Load set primary method based on the API response
  if (primaryMethod === '') {
    let primaryMethod = phoneNumber;
    if (defaultContactType === EMAIL) primaryMethod = email;
    if (defaultContactType === VOICE) primaryMethod = voice.unformattedvalue;
    setPrimaryMethodOriginal(primaryMethod);
    setPrimaryMethod(getMaskedValue({ challengeType: defaultContactType, data }));
    setPrimaryMethodType(defaultContactType);
  }

  return (
    <section className={classes.modalWrapper}>
      <ModalNotification
        variant={variant}
        show={open}
        onHide={onHide}
        suppressIcon={false}
        disableBackdropClick
      >
        <form onSubmit={onSubmit} data-qa="form">
          {challengeType === OTP && (
            <OtpForm
              challengeErrorMessage={challengeErrorMessage}
              classes={classes}
              data={data}
              deviceChallenge={deviceChallenge}
              isSubmitting={isSubmitting}
              onChange={onChange}
              onKeyDown={onKeyDown}
              locale={locale}
              onCancel={onCancel}
              onChecked={onChecked}
              onSubmitForm={onSubmitForm}
              otpCode={otpCode}
              primaryMethod={primaryMethod}
              primaryMethodType={primaryMethodType}
              rememberMeFlag={rememberMeFlag}
              resendCode={resendCode}
              sendCodeToAltMethod={sendCodeToAltMethod}
              setShowAltMethod={setShowAltMethod}
              showAltMethod={showAltMethod}
              validate={validate}
              altMethodMessage={altMethodMessage}
            />
          )}
          {[VIP_CHALLENGE, VIP_ACTIVATION, VIP_UNAVAILABLE].includes(challengeType) && (
            <VipForm
              locale={locale}
              classes={classes}
              data={data}
              deviceChallenge={deviceChallenge}
              isSubmitting={isSubmitting}
              onCancel={onCancel}
              onChange={onChange}
              onChecked={onChecked}
              onKeyDown={onKeyDown}
              onSubmitForm={onSubmitForm}
              rememberMeFlag={rememberMeFlag}
              vipTokens={vipTokens}
              validate={validate}
            />
          )}
          {challengeType?.includes(RADIUS) && (
            <RadiusForm
              locale={locale}
              classes={classes}
              data={data}
              deviceChallenge={deviceChallenge}
              isSubmitting={isSubmitting}
              onCancel={onCancel}
              onChange={onChange}
              onChecked={onChecked}
              onKeyDown={onKeyDown}
              onSubmitForm={onSubmitForm}
              radiusToken={radiusToken}
              rememberMeFlag={rememberMeFlag}
              validate={validate}
            />
          )}
        </form>
      </ModalNotification>
    </section>
  );
};

ChallengeManagerComponent.defaultProps = {
  challengeErrorMessage: false,
  data: {},
  onCancel() {},
  onHide() {},
  logout() {},
  clearGrids() {},
  open: false,
  onSubmit() {},
  variant: 'error',
  deviceChallenge: false,
  rememberMe() {},
  rememberMeFlag: false
};

ChallengeManagerComponent.propTypes = {
  challengeErrorMessage: PropTypes.bool,
  data: PropTypes.shape({}),
  onCancel: PropTypes.func,
  onHide: PropTypes.func,
  clearGrids: PropTypes.func,
  logout: PropTypes.func,
  open: PropTypes.bool,
  onSubmit: PropTypes.func,
  variant: PropTypes.string,
  deviceChallenge: PropTypes.bool,
  rememberMe: PropTypes.func,
  rememberMeFlag: PropTypes.bool,
  validate: PropTypes.func.isRequired
};

const Themed = withTheme(ChallengeManagerComponent);
const Validations = withFormProvider(Themed);

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
  logout: (...params) => dispatch(logout(...params)),
  clearGrids: () => clearGrids(dispatch)
});

const Connected = connect(null, mapDispatchToProps)(Validations);
export default Connected;
